import React, { useState } from 'react';
import { Modal, Input, Typography, DatePicker, theme } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';
import { message } from '../../services/toastMessage';
import { quoteServiceRequest } from '../../services/ServiceRequestService';
import { IServiceRequest } from '../../interfaces/heavyDutyMachinery/IServiceRequest';
import { IQuoteDraftLine } from '../../interfaces/heavyDutyMachinery/IQuoteDraft';
import {
  IQuoteDraftEditLine,
  draftLineToEditLine,
  editLinesTotal,
  emptyEditLine,
  mergeDraftedLines,
  toEditLine,
  toProposedLines,
} from '../../helpers/quoteDraftHelper';
import { latestServiceQuote } from '../../helpers/serviceRequestHelper';
import { editableQuoteLines, visitFeeLockedLines } from '../../helpers/visitFeeHelper';
import { TRANSLATIONS } from '../../constants/translations';
import { translate } from '../../helpers/translationHelper';
import QuoteLineEditor from './QuoteLineEditor';
import QuoteDraftAssistant from './QuoteDraftAssistant';
import QuoteVoiceEdit from './QuoteVoiceEdit';

const { TextArea } = Input;
const { Text } = Typography;

interface ServiceQuoteModalProps {
  request: IServiceRequest;
  open: boolean;
  onClose: () => void;
  onQuoted: (request: IServiceRequest) => void;
}

/**
 * The professional putting a price on a job described in words.
 *
 * Everything in here already existed for the deferred-booking counter-offer:
 * the line editor, the assistant that drafts from a description, the voice
 * editing. That is the point -- the customer's own words go straight into the
 * assistant as the job description, and the provider's rate card does the
 * pricing. Writing a quote for "se me moja la pared del baño" becomes reading
 * one and fixing what is wrong with it.
 *
 * The window is optional and asked for here rather than afterwards, because
 * accepting a price without knowing when is half a deal. Skipping it is a
 * first-class path: the visit is then fixed after acceptance.
 */
const ServiceQuoteModal: React.FC<ServiceQuoteModalProps> = ({ request, open, onClose, onQuoted }) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();

  const current = latestServiceQuote(request);

  // The visit is not theirs to price. It comes out of the editable set and is
  // shown locked above it -- including on the FIRST quote, where no saved
  // version carries it yet and only the request's own snapshot knows about it.
  // Leave it out and the professional writes a quote whose total is short of
  // what the customer is about to be asked for. See visitFeeHelper.
  const lockedLines = visitFeeLockedLines(
    current?.lines,
    request.visitFeeAmount,
    translate(t, TRANSLATIONS.VISIT_FEE_LINE_TITLE),
  );
  const startingLines = () => editableQuoteLines(current?.lines).map(toEditLine);

  const [lines, setLines] = useState<IQuoteDraftEditLine[]>(startingLines);
  const [reason, setReason] = useState('');
  const [window, setWindow] = useState<[Dayjs, Dayjs] | null>(
    current?.proposedFrom && current?.proposedTo
      ? [dayjs(current.proposedFrom), dayjs(current.proposedTo)]
      : null
  );
  const [undoableLines, setUndoableLines] = useState<IQuoteDraftEditLine[]>();
  const [submitting, setSubmitting] = useState(false);

  const isRevision = (request.quoteHistory?.length ?? 0) > 0;
  const lockedTotal = lockedLines.reduce((sum, line) => sum + line.amount, 0);
  const total = lockedTotal + editLinesTotal(lines);
  const hasDraft = lines.some((line) => line.generated);

  const handleDrafted = (drafted: IQuoteDraftLine[]) =>
    setLines((previous) => [...previous.filter((line) => !line.generated), ...drafted.map(draftLineToEditLine)]);

  const handleVoiceEdit = (drafted: IQuoteDraftLine[]) =>
    setLines((previous) => {
      setUndoableLines(previous);
      return mergeDraftedLines(previous, drafted);
    });

  const handleClose = () => {
    setLines(startingLines());
    setReason('');
    setUndoableLines(undefined);
    onClose();
  };

  const handleSubmit = async () => {
    const editable = lines.length > 0 ? lines : [emptyEditLine()];
    if (editable.some((line) => !line.title.trim())) {
      message.error(translate(t, TRANSLATIONS.QUOTE_EDITOR_TITLE_REQUIRED));
      return;
    }
    if (total <= 0) {
      message.error(translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_QUOTE_PROPOSE_AMOUNT_REQUIRED));
      return;
    }
    if (isRevision && !reason.trim()) {
      message.error(translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_QUOTE_PROPOSE_REASON_REQUIRED));
      return;
    }

    setSubmitting(true);
    try {
      const updated = await quoteServiceRequest(request.id, {
        lines: toProposedLines(lines),
        reason: isRevision ? reason.trim() : undefined,
        // Both ends or neither -- the server refuses half a window, and a
        // half-open one would mean nothing to the customer either.
        proposedFrom: window?.[0]?.toISOString(),
        proposedTo: window?.[1]?.toISOString(),
      });
      message.success(translate(t, TRANSLATIONS.SERVICE_REQUEST_QUOTED_TOAST));
      onQuoted(updated);
      handleClose();
    } catch (error: any) {
      message.error(error.message || translate(t, TRANSLATIONS.SERVICE_REQUEST_ACTION_ERROR));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title={translate(t, isRevision ? TRANSLATIONS.SERVICE_REQUEST_REQUOTE : TRANSLATIONS.SERVICE_REQUEST_QUOTE_IT)}
      open={open}
      onCancel={handleClose}
      onOk={handleSubmit}
      okText={translate(t, TRANSLATIONS.SERVICE_REQUEST_SEND_QUOTE)}
      confirmLoading={submitting}
      width={640}
      styles={{ body: { maxHeight: '70vh', overflowY: 'auto' } }}
    >
      {/* Their own words, kept in front of the professional while they price
          it. Scrolling back up to remember what was asked for is how a line
          gets left out. */}
      <div style={{ background: token.colorFillQuaternary, borderRadius: 12, padding: 12, marginBottom: 14 }}>
        <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 4 }}>
          {translate(t, TRANSLATIONS.SERVICE_REQUEST_WHAT_THEY_ASKED)}
        </Text>
        <Text style={{ fontSize: 13, whiteSpace: 'pre-wrap' }}>{request.description}</Text>
      </div>

      <QuoteDraftAssistant onDrafted={handleDrafted} hasDraft={hasDraft} defaultJob={request.description} />

      {lines.length > 0 && <QuoteVoiceEdit lines={lines} onApplied={handleVoiceEdit} />}

      {undoableLines && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '8px 12px', marginBottom: 10, borderRadius: 12, background: token.colorFillQuaternary }}>
          <Text type="secondary" style={{ fontSize: 12, flex: 1 }}>
            {translate(t, TRANSLATIONS.QUOTE_VOICE_EDIT_APPLIED)}
          </Text>
          <button
            type="button"
            onClick={() => {
              setLines(undoableLines);
              setUndoableLines(undefined);
            }}
            style={{ border: 'none', background: 'transparent', color: token.colorPrimary, cursor: 'pointer', fontSize: 12 }}
          >
            {translate(t, TRANSLATIONS.QUOTE_VOICE_EDIT_UNDO)}
          </button>
        </div>
      )}

      <QuoteLineEditor lines={lines} onChange={setLines} lockedLines={lockedLines} currency={request.currencyCode} />

      <Text type="secondary" style={{ display: 'block', fontSize: 12, marginBottom: 6 }}>
        {translate(t, TRANSLATIONS.SERVICE_REQUEST_WINDOW_LABEL)}
      </Text>
      <DatePicker.RangePicker
        showTime={{ format: 'HH:mm' }}
        value={window}
        onChange={(value) => setWindow(value as [Dayjs, Dayjs] | null)}
        disabledDate={(date) => date.isBefore(dayjs(), 'day')}
        style={{ width: '100%', borderRadius: 12, marginBottom: 14 }}
      />

      {isRevision && (
        <>
          <Text type="secondary" style={{ display: 'block', fontSize: 12, marginBottom: 6 }}>
            {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_QUOTE_PROPOSE_REASON_LABEL)}
          </Text>
          <TextArea
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_QUOTE_PROPOSE_REASON_PLACEHOLDER)}
            maxLength={1000}
          />
        </>
      )}
    </Modal>
  );
};

export default ServiceQuoteModal;
